const MAX_RESULTS = 10;

type SearchGroup<TItem extends { id: number | string }> = {
  label: string;
  items: TItem[];
};

/**
 * Filters and sorts quicksearch groups based on a query string and active group selection.
 *
 * Items are filtered by checking that every whitespace-separated search term
 * appears in the combined label + Object.values(item) text. Within each group, items
 * whose `id` matches any search term are sorted first. Results are capped at
 * {@link MAX_RESULTS} total items across all groups.
 *
 * @template TItem - Item type that must have a numeric or string `id`.
 * @template TGroup - Group type extending {@link SearchGroup}.
 * @param groups - All available search groups.
 * @param selectedGroups - Labels of groups to restrict results to; pass an
 *   empty array to include all groups.
 * @param lowerQuery - The search query, already lowercased.
 * @returns A filtered, sorted copy of the groups containing only matching
 *   items. Groups with no matching items are omitted.
 */
function filterAndSortGroups<
  TItem extends { id: number | string },
  TGroup extends SearchGroup<TItem>
>(groups: TGroup[], selectedGroups: string[], lowerQuery: string): TGroup[] {
  const searchTerms = lowerQuery.split(/\s+/).filter((term) => term);
  let remainingSlots = MAX_RESULTS;

  const activeGroups = groups.filter((group) =>
    selectedGroups.length > 0 ? selectedGroups.includes(group.label) : true
  );

  const groupsWithFilteredItems = activeGroups.map((group) => {
    const matchingItems = group.items.filter((item) => {
      const itemText =
        `${group.label} ${Object.values(item).join(" ")}`.toLowerCase();
      return searchTerms.every((term) => itemText.includes(term));
    });

    const sortedItems = matchingItems.sort((a, b) => {
      const idMatchA = searchTerms.some((term) => String(a.id).includes(term));
      const idMatchB = searchTerms.some((term) => String(b.id).includes(term));
      return Number(idMatchB) - Number(idMatchA);
    });

    const slicedItems = sortedItems.slice(0, remainingSlots);
    remainingSlots -= slicedItems.length;

    return { ...group, items: slicedItems };
  });

  return groupsWithFilteredItems.filter((group) => group.items.length > 0);
}

export { filterAndSortGroups };
export type { SearchGroup };
